/**
 * Grid preview component
 * Shows preview of grid cells on the map
 */

import { useMemo } from 'react';
import { Typography, Space } from 'antd';
import { MapContainer } from '@/components/maps/MapContainer';
import { GridOverlay } from '@/components/maps/GridOverlay';
import { MapStats } from '@/components/maps/MapLegend';
import type { Bounds, GridPreview as GridPreviewType, GridConfig, PolygonCoordinates } from '@/types/grid';
import { generateGridPreview, calculateArea, zoomToCellSize } from '@/utils/mapUtils';
import { boundsToLeafletBounds } from '@/utils/mapUtils';
import { formatNumber } from '@/utils/formatters';

const { Text } = Typography;

interface GridPreviewProps {
  bounds: Bounds;
  zoomLevel: number;
  height?: number;
  showGrid?: boolean;
  showStats?: boolean;
  /** Optional polygon coordinates for freehand selection display */
  polygon?: PolygonCoordinates;
}

export function GridPreview({
  bounds,
  zoomLevel,
  height = 300,
  showGrid = true,
  showStats = true,
  polygon,
}: GridPreviewProps) {
  const gridConfig: GridConfig = useMemo(
    () => ({
      zoomLevel,
      cellSizeKm: zoomToCellSize(zoomLevel),
      overlapPercent: 10,
    }),
    [zoomLevel]
  );

  const gridPreview = useMemo(
    () => generateGridPreview(bounds, gridConfig, polygon),
    [bounds, gridConfig, polygon]
  );

  const mapBounds = useMemo(
    () => boundsToLeafletBounds(bounds),
    [bounds]
  );

  const areaKm2 = useMemo(() => calculateArea(bounds), [bounds]);

  const stats = showStats
    ? [
        { label: 'Cells', value: formatNumber(gridPreview.totalCells) },
        { label: 'Area', value: `${areaKm2.toFixed(1)} km²` },
        { label: 'Grid', value: `${gridPreview.rows}×${gridPreview.columns}` },
      ]
    : [];

  return (
    <div style={{ position: 'relative' }}>
      <MapContainer
        height={height}
        bounds={mapBounds}
        scrollWheelZoom={false}
        dragging={true}
      >
        {showGrid && (
          <GridOverlay
            bounds={bounds}
            gridPreview={gridPreview}
            polygon={polygon}
            showGrid
            opacity={0.15}
          />
        )}

        {showStats && <MapStats stats={stats} position="topright" />}
      </MapContainer>
    </div>
  );
}

/**
 * Compact grid info display
 */
export function GridInfo({
  bounds,
  zoomLevel,
}: {
  bounds: Bounds;
  zoomLevel: number;
}) {
  const gridConfig: GridConfig = useMemo(
    () => ({
      zoomLevel,
      cellSizeKm: zoomToCellSize(zoomLevel),
      overlapPercent: 10,
    }),
    [zoomLevel]
  );

  const gridPreview = useMemo(
    () => generateGridPreview(bounds, gridConfig),
    [bounds, gridConfig]
  );

  const areaKm2 = useMemo(() => calculateArea(bounds), [bounds]);
  const estimatedMinutes = Math.ceil((gridPreview.totalCells * 3) / 60);

  return (
    <Space direction="vertical" size={4}>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 24 }}>
        <Text type="secondary">Area:</Text>
        <Text strong>{areaKm2.toFixed(1)} km²</Text>
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 24 }}>
        <Text type="secondary">Grid cells:</Text>
        <Text strong>{formatNumber(gridPreview.totalCells)}</Text>
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 24 }}>
        <Text type="secondary">Grid size:</Text>
        <Text strong>
          {gridPreview.rows} × {gridPreview.columns}
        </Text>
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 24 }}>
        <Text type="secondary">Est. time:</Text>
        <Text strong>~{estimatedMinutes} min</Text>
      </div>
    </Space>
  );
}

/**
 * Mini grid visualization
 */
export function GridVisualization({
  rows,
  columns,
  size = 100,
}: {
  rows: number;
  columns: number;
  size?: number;
}) {
  // Limit visualization size
  const maxDimension = 20;
  const displayRows = Math.min(rows, maxDimension);
  const displayCols = Math.min(columns, maxDimension);

  const cellSize = Math.max(2, Math.floor(size / Math.max(displayRows, displayCols)));

  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: `repeat(${displayCols}, ${cellSize}px)`,
        gridTemplateRows: `repeat(${displayRows}, ${cellSize}px)`,
        gap: 1,
        padding: 8,
        background: 'var(--color-bg-spotlight)',
        borderRadius: 8,
        width: 'fit-content',
      }}
    >
      {Array.from({ length: displayRows * displayCols }).map((_, index) => (
        <div
          key={index}
          style={{
            width: cellSize,
            height: cellSize,
            background: 'var(--color-primary)',
            opacity: 0.3,
            borderRadius: 1,
          }}
        />
      ))}
    </div>
  );
}

export default GridPreview;
